/**
 * Marks color swatches swatch--light or swatch--dark from whatever face is showing
 * (image, CSS image, or background color) so the unavailable diagonal stays readable.
 */
(() => {
  const SELECTOR = [
    '.opt-label--swatch',
    '.opt-label--image',
    '.product-block-options--swatch .product-block-options__item'
  ].join(', ');

  const SAMPLE = 8;
  const imageCache = new Map();
  let queued = [];
  let frame = 0;

  function luminance(r, g, b) {
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }

  function parseRgb(color) {
    if (!color || color === 'transparent') return null;
    const match = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)/i);
    if (!match) return null;
    let alpha = 1;
    if (match[4] !== undefined) {
      alpha = match[4].endsWith('%') ? parseFloat(match[4]) / 100 : parseFloat(match[4]);
    }
    if (alpha < 0.05) return null;
    return [Number(match[1]), Number(match[2]), Number(match[3])];
  }

  function hexToRgb(hex) {
    let value = hex.replace('#', '');
    if (value.length === 3) {
      value = value.split('').map((char) => char + char).join('');
    }
    if (value.length !== 6) return null;
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16)
    ];
  }

  function averageLuminance(colors) {
    if (!colors.length) return null;
    const total = colors.reduce((sum, [r, g, b]) => sum + luminance(r, g, b), 0);
    return total / colors.length;
  }

  /**
   * @returns {number|string|null} luminance, image URL, or nothing usable
   */
  function parseBackgroundImage(value) {
    const trimmed = (value || '').trim();
    if (!trimmed || trimmed === 'none') return null;

    const urlMatch = trimmed.match(/url\(\s*(['"]?)(.*?)\1\s*\)/i);
    if (urlMatch && urlMatch[2]) return urlMatch[2];

    const colors = [];
    const hexes = trimmed.match(/#(?:[0-9a-fA-F]{3}){1,2}\b/g) || [];
    hexes.forEach((hex) => {
      const rgb = hexToRgb(hex);
      if (rgb) colors.push(rgb);
    });
    const rgbs = trimmed.match(/rgba?\([^)]+\)/g) || [];
    rgbs.forEach((color) => {
      const rgb = parseRgb(color);
      if (rgb) colors.push(rgb);
    });
    return averageLuminance(colors);
  }

  function shrinkUrl(url) {
    try {
      const absolute = url.startsWith('//') ? `${window.location.protocol}${url}` : url;
      const parsed = new URL(absolute, window.location.href);
      if (/cdn\.shopify\.com|shopifycdn\.com/i.test(parsed.hostname)) {
        parsed.searchParams.set('width', '32');
      }
      return parsed.toString();
    } catch (err) {
      return url;
    }
  }

  function sampleImage(url) {
    const key = shrinkUrl(url);
    if (imageCache.has(key)) return imageCache.get(key);

    const promise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = SAMPLE;
          canvas.height = SAMPLE;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
          const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);
          let sum = 0;
          let count = 0;
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 10) continue;
            sum += luminance(data[i], data[i + 1], data[i + 2]);
            count += 1;
          }
          resolve(count ? sum / count : null);
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = key;
    });

    imageCache.set(key, promise);
    return promise;
  }

  function imageUrl(img) {
    const manual = img.getAttribute('data-manual-src');
    if (manual) return manual;
    const src = img.currentSrc || img.getAttribute('src') || '';
    if (!src || src.startsWith('data:')) return '';
    return src;
  }

  function visibleImage(el) {
    const img = el.querySelector('img');
    if (!img) return null;
    const style = getComputedStyle(img);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return null;
    return img;
  }

  function paintOf(image, color) {
    const parsed = parseBackgroundImage(image);
    if (parsed != null) return parsed;
    return colorLuminance(color);
  }

  function colorLuminance(color) {
    if (!color) return null;
    const trimmed = color.trim();
    const rgb = parseRgb(trimmed) || (trimmed.startsWith('#') ? hexToRgb(trimmed) : null);
    if (rgb) return luminance(rgb[0], rgb[1], rgb[2]);

    const probe = document.createElement('canvas').getContext('2d');
    if (!probe) return null;
    probe.fillStyle = '#010101';
    probe.fillStyle = trimmed;
    if (probe.fillStyle === '#010101') return null;
    const parsed = hexToRgb(probe.fillStyle);
    if (!parsed) return null;
    return luminance(parsed[0], parsed[1], parsed[2]);
  }

  function readPaint(el) {
    const style = getComputedStyle(el);
    const fromImageVar = parseBackgroundImage(style.getPropertyValue('--swatch-background-image'));
    if (fromImageVar != null) return fromImageVar;

    const fromColorVar = colorLuminance(style.getPropertyValue('--swatch-background-color'));
    if (fromColorVar != null) return fromColorVar;

    const before = getComputedStyle(el, '::before');
    const beforePainted = before.content !== 'none' && before.content !== 'normal';
    if (beforePainted && parseFloat(before.opacity) >= 0.9) {
      const beforePaint = paintOf(before.backgroundImage, before.backgroundColor);
      if (beforePaint != null) return beforePaint;
    }

    return paintOf(style.backgroundImage, style.backgroundColor);
  }

  function applyClass(el, value) {
    el.classList.remove('swatch--light', 'swatch--dark');
    el.classList.add(value > 0.5 ? 'swatch--light' : 'swatch--dark');
    el.dataset.swatchContrast = '1';
  }

  function bindImageWatch(el, img) {
    if (img.dataset.swatchContrastBound) return;
    img.dataset.swatchContrastBound = '1';
    const onChange = () => {
      const next = imageUrl(img);
      if (next && next !== el.dataset.swatchContrastSrc) classify(el);
    };
    img.addEventListener('load', onChange);
    img.addEventListener('error', onChange);
  }

  function classify(el) {
    if (!(el instanceof Element) || !el.matches(SELECTOR)) return;

    const img = visibleImage(el);
    const src = img ? imageUrl(img) : '';
    const key = src || 'color';
    const state = el.dataset.swatchContrast;

    if ((state === '1' || state === 'skip' || state === 'pending') && el.dataset.swatchContrastSrc === key) {
      return;
    }

    if (img && !src) {
      bindImageWatch(el, img);
      return;
    }

    el.dataset.swatchContrast = 'pending';
    el.dataset.swatchContrastSrc = key;

    const finish = (lum) => {
      if (el.dataset.swatchContrastSrc !== key) return;
      if (lum == null || Number.isNaN(lum)) {
        el.dataset.swatchContrast = 'skip';
        return;
      }
      applyClass(el, lum);
    };

    if (src) {
      bindImageWatch(el, img);
      sampleImage(src).then(finish);
      return;
    }

    const paint = readPaint(el);
    if (typeof paint === 'string') {
      sampleImage(paint).then(finish);
      return;
    }
    finish(paint);
  }

  function scan(root) {
    const scope = root && root.nodeType === 1 ? root : document;
    if (scope.matches && scope.matches(SELECTOR)) classify(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(SELECTOR).forEach(classify);
  }

  function queueScan(node) {
    queued.push(node);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      const nodes = queued;
      queued = [];
      frame = 0;
      nodes.forEach(scan);
    });
  }

  function init() {
    scan(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) queueScan(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.theme = window.theme || {};
  window.theme.applySwatchContrast = scan;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
